import {
    Platform
} from "react-native";
import type {
    PopoverFocusMoveConstantType,
    PopoverTabKeyConstantType,
    PopoverSize
} from "./type";

export const POPOVER_SIZE_RATIOS: Record<PopoverSize, number> = {
    medium: 1.5,
    small: 1,
    large: 2
};

export const POPOVER_NO_COLLISION_PADDING = -100000;

const POPOVER_TABBABLE_SELECTOR = "a[href], area[href], button, input, select, textarea, iframe, object, embed, summary, audio[controls], video[controls], [tabindex], [contenteditable]";

export const getPopoverElement = (node: unknown): HTMLElement | null => {
    if(typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return null;
    }

    return node;
};

export const getPopoverTabbables = (container: ParentNode): Array<HTMLElement> => {
    return Array.from(container.querySelectorAll<HTMLElement>(POPOVER_TABBABLE_SELECTOR)).filter((element) => {
        return element.tabIndex >= 0 && !element.matches(":disabled") && element.getClientRects().length > 0;
    });
};

export const getPopoverTriggerElement = (triggerContainer: HTMLElement | null): HTMLElement | null => {
    if(!triggerContainer) {
        return null;
    }

    return getPopoverTabbables(triggerContainer)[0] ?? null;
};

export const focusPopoverElement = (element: HTMLElement) => {
    element.focus({
        preventScroll: true
    });
};

export const focusPopoverTrigger = ({
    triggerElement: triggerContainer
}: Pick<PopoverFocusMoveConstantType, "triggerElement">): boolean => {
    const triggerElement = getPopoverTriggerElement(triggerContainer);

    if(!triggerElement) {
        return false;
    }

    focusPopoverElement(triggerElement);

    return true;
};

export const focusPopoverAfterTrigger = ({
    triggerElement: triggerContainer,
    surfaceElement
}: PopoverFocusMoveConstantType): boolean => {
    const triggerElement = getPopoverTriggerElement(triggerContainer);

    if(!triggerElement) {
        return false;
    }

    const tabbables = getPopoverTabbables(triggerElement.ownerDocument).filter((element) => !surfaceElement.contains(element));

    const nextElement = tabbables[tabbables.indexOf(triggerElement) + 1];

    if(!nextElement) {
        return false;
    }

    focusPopoverElement(nextElement);

    return true;
};

export const handlePopoverTabKeyDown = ({
    surfaceRef,
    triggerRef,
    onLeave,
    event
}: PopoverTabKeyConstantType) => {
    const surfaceElement = getPopoverElement(surfaceRef.current);

    if(Platform.OS !== "web" || !surfaceElement || event.key !== "Tab") {
        return;
    }

    const tabbables = getPopoverTabbables(surfaceElement);

    const activeElement = surfaceElement.ownerDocument.activeElement;

    const triggerElement = getPopoverElement(triggerRef.current);

    if(event.shiftKey) {
        if(activeElement !== tabbables[0] && activeElement !== surfaceElement) {
            return;
        }

        event.preventDefault();

        focusPopoverTrigger({
            triggerElement
        });

        return;
    }

    if(activeElement !== tabbables[tabbables.length - 1] && tabbables.length) {
        return;
    }

    event.preventDefault();

    focusPopoverAfterTrigger({
        surfaceElement,
        triggerElement
    });

    onLeave();
};
